import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { feedbackApi } from '../lib/api';
import { FlagIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

interface FlagFeedbackButtonProps {
  reportId: string;
  flagId: string;
  flagName: string;
  existingFeedback?: {
    id: string;
    feedbackType: string;
    status: string;
  } | null;
}

const FEEDBACK_TYPE_OPTIONS = [
  {
    value: 'INCORRECT_RULE' as const,
    label: 'Incorrect Rule — This rule was applied incorrectly to this content',
  },
  {
    value: 'DOES_NOT_APPLY' as const,
    label: 'Does Not Apply — This regulation does not apply in this context',
  },
  {
    value: 'MISSING_DISCLOSURE' as const,
    label: 'Missing Disclosure — The disclosure is actually present but wasn\'t detected',
  },
  {
    value: 'OTHER' as const,
    label: 'Other',
  },
];

const FEEDBACK_TYPE_LABELS: Record<string, string> = {
  INCORRECT_RULE: 'Incorrect Rule',
  DOES_NOT_APPLY: 'Does Not Apply',
  MISSING_DISCLOSURE: 'Missing Disclosure',
  OTHER: 'Other',
};

export default function FlagFeedbackButton({
  reportId,
  flagId,
  existingFeedback,
}: FlagFeedbackButtonProps) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<string>('');
  const [note, setNote] = useState('');

  const submitMutation = useMutation({
    mutationFn: (data: {
      reportId: string;
      flagId: string;
      feedbackType: 'INCORRECT_RULE' | 'DOES_NOT_APPLY' | 'MISSING_DISCLOSURE' | 'OTHER';
      note?: string;
    }) => feedbackApi.submitFeedback(data),
    onSuccess: () => {
      toast.success('Thanks — your feedback has been recorded and will be reviewed.');
      queryClient.invalidateQueries({ queryKey: ['feedback', reportId] });
      setIsOpen(false);
      setFeedbackType('');
      setNote('');
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.error || 'Failed to submit feedback';
      if (error.response?.status === 409) {
        toast.error('You have already submitted feedback for this flag.');
        queryClient.invalidateQueries({ queryKey: ['feedback', reportId] });
      } else {
        toast.error(message);
      }
    },
  });

  const handleSubmit = () => {
    if (!feedbackType) {
      toast.error('Please select a feedback type');
      return;
    }

    submitMutation.mutate({
      reportId,
      flagId,
      feedbackType: feedbackType as any,
      note: note.trim() || undefined,
    });
  };

  // Already submitted state
  if (existingFeedback) {
    return (
      <div className="mt-3 flex items-center gap-1.5 text-xs text-green-700">
        <CheckCircleIcon className="h-3.5 w-3.5" />
        <span>
          Feedback submitted ({FEEDBACK_TYPE_LABELS[existingFeedback.feedbackType] || existingFeedback.feedbackType})
        </span>
      </div>
    );
  }

  return (
    <div className="mt-3">
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          <FlagIcon className="h-3.5 w-3.5" />
          Report Issue
        </button>
      )}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-2 p-3 bg-white border border-gray-200 rounded-md shadow-sm space-y-3">
              <div>
                <label
                  htmlFor={`feedback-type-${flagId}`}
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  What's wrong with this flag?
                </label>
                <select
                  id={`feedback-type-${flagId}`}
                  value={feedbackType}
                  onChange={(e) => setFeedbackType(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="">Select a reason...</option>
                  {FEEDBACK_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor={`feedback-note-${flagId}`}
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Additional details (optional)
                </label>
                <textarea
                  id={`feedback-note-${flagId}`}
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 1000))}
                  placeholder="Tell us why this flag is incorrect (optional but helps us improve)"
                  rows={2}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <p className="text-right text-xs text-gray-400 mt-0.5">
                  {note.length}/1000
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleSubmit}
                  disabled={submitMutation.isPending || !feedbackType}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitMutation.isPending ? 'Submitting...' : 'Submit Feedback'}
                </button>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setFeedbackType('');
                    setNote('');
                  }}
                  disabled={submitMutation.isPending}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
